import classNames from 'classnames';
import { Spin } from 'lego-on-react';
import { noop } from 'lodash';
import * as React from 'react';
import { AutoSizer, Dimensions } from 'react-virtualized';

import { Bem } from '../../service/Bem/Bem';
import { CanvasPcodeVideoFrameParams, PcodeVideoRendererCanvas } from '../../service/Pcode/PcodeCanvasVideo';

import './PcodeVideoCanvasPreview.css';

export enum LoadingStatus {
    NOT_STARTED = 'NOT_STARTED',
    PROGRESS = 'PROGRESS',
    SUCCESS = 'SUCCESS'
}

export interface PcodeVideoCanvasPreviewProps {
    videoCls?: string;
    onAdEndCallback?: () => void;
    videoPreviewData?: Partial<CanvasPcodeVideoFrameParams>;
    isAudio: boolean;
}

interface PcodeVideoCanvasPreviewState {
    loadingStatus: LoadingStatus;
    externalLoadingStatus: LoadingStatus;
}

const BLOCK = 'pcode-video-canvas-preview';

export class PcodeVideoCanvasPreview extends React.PureComponent<
    PcodeVideoCanvasPreviewProps,
    PcodeVideoCanvasPreviewState
> {
    state: PcodeVideoCanvasPreviewState = {
        loadingStatus: LoadingStatus.NOT_STARTED,
        externalLoadingStatus: LoadingStatus.NOT_STARTED
    };
    private renderNode = React.createRef<HTMLDivElement>();
    private pcodeVideoRenderer: PcodeVideoRendererCanvas | undefined;

    // в ya.mediaAd.bannerAd нет методов для апдейта существующего плеера
    // только хардкор, только перерисовка с нуля
    public onExternalInitPlayer(data: Partial<CanvasPcodeVideoFrameParams>) {
        this.setState({
            loadingStatus: LoadingStatus.PROGRESS
        });
        this.getPcodeVideoRenderer().init(data);
    }

    public onExternalStartPlayer() {
        this.getPcodeVideoRenderer().play();
    }

    public onExternalDestroyPlayer() {
        if (this.pcodeVideoRenderer) {
            this.pcodeVideoRenderer.destroy();
        }
    }

    public onExternalPausePlayer() {
        this.getPcodeVideoRenderer().pause();
    }

    // иногда нужно показать троббер на превью, когда реально загрузки в превью не происходит
    // например - пока грузится файл с компьютера
    public onExternalToggleLoadingStatus({ isLoading }: { isLoading: boolean }) {
        this.setState({
            externalLoadingStatus: isLoading ? LoadingStatus.PROGRESS : LoadingStatus.SUCCESS
        });
    }

    componentDidMount(): void {
        this.initVideoFromPropsData({
            current: this.props.videoPreviewData
        });
    }

    componentDidUpdate(prevProps: Readonly<PcodeVideoCanvasPreviewProps>): void {
        this.initVideoFromPropsData({
            prev: prevProps.videoPreviewData,
            current: this.props.videoPreviewData
        });
    }

    render() {
        const inProgress =
            this.state.loadingStatus === LoadingStatus.PROGRESS ||
            this.state.externalLoadingStatus === LoadingStatus.PROGRESS;

        const { videoCls, isAudio } = this.props;

        return (
            <AutoSizer>
                {(dimensions: Dimensions) => {
                    const { width, height } = this.calcVideoContainerDimensions(dimensions, isAudio ? 1 : 16 / 9);
                    const videoContainerStyle = {
                        width: `${width}px`,
                        height: `${height}px`
                    };
                    return (
                        <div
                            className={classNames(
                                videoCls,
                                Bem.cls({
                                    block: BLOCK,
                                    mods: { loading: inProgress }
                                })
                            )}
                            style={{
                                width: `${dimensions.width}px`,
                                height: `${dimensions.height}px`
                            }}
                        >
                            <div className={Bem.cls(BLOCK, 'spin')}>
                                <Spin size={'l'} progress={inProgress} />
                            </div>
                            <div
                                className={Bem.cls(BLOCK, 'video')}
                                key={'pcode-preview-key'}
                                ref={this.renderNode}
                                style={videoContainerStyle}
                            />
                        </div>
                    );
                }}
            </AutoSizer>
        );
    }

    private initVideoFromPropsData(videoPreviewData: {
        prev?: Partial<CanvasPcodeVideoFrameParams>;
        current?: Partial<CanvasPcodeVideoFrameParams>;
    }) {
        const { prev, current } = videoPreviewData;

        if (current && prev !== current) {
            this.onExternalInitPlayer(current);
        }
    }

    // видео-плеер гарантированно корректно встроится только в родителя с фиксированными размерами
    // в противном случае - результат непредсказуем и зависит от конкретного видео
    private calcVideoContainerDimensions = ({ width, height }: Dimensions, ratio: number): Dimensions => {
        return width / height > ratio ? { width: height * ratio, height } : { width, height: width / ratio };
    };

    private getPcodeVideoRenderer() {
        if (!this.pcodeVideoRenderer) {
            const renderNode = this.renderNode.current;

            if (renderNode) {
                this.pcodeVideoRenderer = new PcodeVideoRendererCanvas(renderNode, {
                    onAdEnd: this.props.onAdEndCallback || noop,
                    onInited: () => {
                        this.setState({
                            loadingStatus: LoadingStatus.SUCCESS
                        });
                    }
                });
            } else {
                throw Error('Node for VideoPlayer is not defined');
            }
        }

        return this.pcodeVideoRenderer;
    }
}
